import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faArrowLeft, faRocket, faGraduationCap, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { getExamUrl } from '../utils/slugify';

const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [examId, setExamId] = useState<string | null>(null);
  const [examName, setExamName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const [allExams, setAllExams] = useState<boolean>(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const examIdParam = searchParams.get('exam_id');
    const allExamsParam = searchParams.get('all_exams') === 'true';
    
    setAllExams(allExamsParam);
    
    if (examIdParam) {
      setExamId(examIdParam);
    }
    
    if (allExamsParam && sessionId) {
      // All exams subscription - redirect to certifications page after a short delay
      setTimeout(() => {
        navigate('/certifications');
      }, 2000);
      setLoading(false);
      return;
    }
    
    if (sessionId && !sessionId.startsWith('mock_')) {
      // Real Stripe session - verify and create subscription if needed
      verifyAndCreateSubscription(sessionId);
    } else {
      // Mock payment or no session ID
      setLoading(false);
    }
  }, [searchParams, navigate]);

  const verifyAndCreateSubscription = async (sessionId: string) => {
    try {
      setVerifying(true);
      setLoading(true);
      
      // Try to verify and create subscription
      const response = await api.post(`/subscriptions/verify/${sessionId}`);
      
      if (response.data.success) {
        setSubscriptionCreated(response.data.subscriptionCreated);
        setError(null);
      } else {
        setError('Failed to verify subscription. Please contact support if the issue persists.');
      }
    } catch (err: any) {
      console.error('Failed to verify subscription:', err);
      
      // If it's a 404 or the session doesn't exist, it might be a mock payment
      if (err.response?.status === 404 || err.response?.status === 400) {
        // Wait a bit more for webhook to process, then check subscription status
        setTimeout(async () => {
          if (examId) {
            try {
              const [statusResponse, examResponse] = await Promise.all([
                api.get(`/subscriptions/${examId}/status`),
                api.get(`/exams/${examId}`).catch(() => null)
              ]);
              if (statusResponse.data.hasActiveSubscription) {
                setSubscriptionCreated(true);
                setError(null);
                if (examResponse?.data?.exam?.name) {
                  setExamName(examResponse.data.exam.name);
                }
              } else {
                setError('Subscription verification failed. Please check your subscription status or contact support.');
              }
            } catch (statusErr) {
              setError('Unable to verify subscription. Please check your subscription status in the dashboard.');
            }
          }
          setLoading(false);
          setVerifying(false);
        }, 3000);
        return;
      }
      
      setError(err.response?.data?.error || 'Failed to verify subscription. Please contact support if the issue persists.');
    } finally {
      if (!error || error.includes('check your subscription')) {
        setTimeout(() => {
          setLoading(false);
          setVerifying(false);
        }, 1000);
      } else {
        setLoading(false);
        setVerifying(false);
      }
    }
  };

  if (loading || verifying) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <h2 className="gp-page-title mb-2">
            {verifying ? 'Verifying your subscription...' : 'Processing your subscription...'}
          </h2>
          <p className="text-sm gp-text-muted">
            {verifying
              ? 'Please wait while we verify and activate your premium access.'
              : 'Please wait while we activate your premium access.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page flex items-center justify-center py-6 px-4">
      <div className="w-full max-w-md">
        <div className="gp-card text-center">
          {error ? (
            <>
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ background: 'var(--gp-accent-soft)' }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-3xl"
                  style={{ color: 'var(--gp-accent)' }}
                  aria-hidden="true"
                />
              </div>
              <h1 className="gp-page-title mb-3">Verification Issue</h1>
              <p className="text-sm gp-text-muted mb-5">{error}</p>
              <div className="space-y-2">
                {examId && (
                  <button
                    type="button"
                    onClick={() => examId && examName ? navigate(getExamUrl(parseInt(examId), examName)) : examId && navigate(`/exam/${examId}`)}
                    className="w-full gp-btn-primary"
                  >
                    <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
                    <span>Try Accessing Exam</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="w-full gp-btn-primary"
                >
                  <FontAwesomeIcon icon={faRocket} aria-hidden="true" />
                  <span>Go to Dashboard</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/billing')}
                  className="w-full gp-btn-ghost"
                >
                  <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                  <span>Check Billing History</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ background: 'var(--gp-success-soft)' }}
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-3xl"
                  style={{ color: 'var(--gp-success)' }}
                  aria-hidden="true"
                />
              </div>
              <h1 className="gp-page-title mb-3">Subscription Successful!</h1>
              <p className="text-sm gp-text-muted mb-2">
                {allExams
                  ? 'Your premium access to all certifications has been activated!'
                  : 'Your premium access has been activated.'}
              </p>
              {subscriptionCreated && (
                <p className="text-sm font-medium mb-4" style={{ color: 'var(--gp-success)' }}>
                  ✓ Subscription activated successfully
                </p>
              )}
              <p className="text-sm gp-text-muted mb-5">
                You can now access all premium questions and advanced features.
              </p>
              <div className="space-y-2">
                {allExams ? (
                  <button
                    type="button"
                    onClick={() => navigate('/certifications')}
                    className="w-full gp-btn-primary"
                  >
                    <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
                    <span>Browse Certifications</span>
                  </button>
                ) : examId ? (
                  <button
                    type="button"
                    onClick={() => examId && examName ? navigate(getExamUrl(parseInt(examId), examName)) : examId && navigate(`/exam/${examId}`)}
                    className="w-full gp-btn-primary"
                  >
                    <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
                    <span>Start Practicing</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="w-full gp-btn-primary"
                >
                  <FontAwesomeIcon icon={faRocket} aria-hidden="true" />
                  <span>Go to Dashboard</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full gp-btn-ghost"
                >
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                  <span>Go Back</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
